import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  CreateUpdateSavedPdfDtoPdfTemplateTypeEnum,
  GetSupportingExaminationDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { TemplateType } from '../../../common/spdCore/validation/schemas';

import { SigningInProgressModal } from './SigningInProgressModal';

interface MultipleReferralSigningProps {
  onSignedFinished: (
    errors: string[],
    allCount: number,
    errorCount: number,
  ) => Promise<void> | void | undefined;
  userUid: string;
}

export const MultipleReferralSigning: React.FC<
  MultipleReferralSigningProps
> = props => {
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [sendSummary, setSendSummary] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [allCount, setAllCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [progress, setProgress] = useState<string>('');
  const auth = useAuth();
  const spd = useSpdCore();
  const szafir = useSzafir();

  useEffect(() => {
    if (sendSummary) {
      void props.onSignedFinished(errors, allCount, errorCount);
      setSendSummary(false);
      setProgress('');
      setErrors([]);
      setAllCount(0);
      setErrorCount(0);
    }
  }, [errors, errorCount, allCount, props, sendSummary]);

  const onSign = async () => {
    setSigningInProgress(true);
    let referrals: GetSupportingExaminationDto[] = [];
    try {
      referrals = await spd.support.getAllForUserToSign(props.userUid);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd pobierania skierowania dla userUid: ${props.userUid}`,
        ...errors,
      ]);
      setSigningInProgress(false);
      setSendSummary(true);
      return;
    }

    setAllCount(referrals.length);
    for (let index = 0; index < referrals.length; index++) {
      const referral = referrals[index];
      setProgress(`podpisywanie ${index + 1} z ${referrals.length}`);
      await signSaveAndSend(referral);
    }
    setSigningInProgress(false);
    setSendSummary(true);
  };

  const signSaveAndSend = async (referral: GetSupportingExaminationDto) => {
    try {
      const taskList = await spd.szafir.getTaskListForSupportingExamination(
        referral.id,
      );
      const [result, documentId] = await new Promise<[Buffer | null, string]>(
        (resolve, reject) => {
          void szafir.signDocument(
            taskList,
            auth.userProfile?.pin || '',
            referral.id,
            '',
            false,
            (result, documentId) => resolve([result, documentId]),
            (error, documentId) => {
              signedFailed(error, documentId);
              reject(new Error(error));
            },
          );
        },
      );
      await saveSignedPdf(result, documentId);
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd podpisywania skierowania o id: ${referral.id} z badania ${referral.exam?.number} - `,
        ...errors,
      ]);
    }
  };

  const saveSignedPdf = async (result: Buffer | null, documentId: string) => {
    await spd.savedPdfs
      .savePdfFileForReferral({
        entityId: documentId,
        file: result,
        pdfTemplateType:
          TemplateType.ClinicReferral as unknown as CreateUpdateSavedPdfDtoPdfTemplateTypeEnum,
      })
      .then(async () => {
        await spd.emails.sendReferralByEmail(documentId);
      })
      .catch(async response => {
        setErrorCount(prevCount => prevCount + 1);
        const errors = await responseErrors(response as ResponseError);
        setErrors(prevArray => [
          ...prevArray,
          `Błąd podpisywania dokumentu o id: ${documentId} - `,
          ...errors,
        ]);
      });
  };

  const signedFailed = (error: string, documentId: string) => {
    setErrorCount(prevCount => prevCount + 1);
    setErrors(prevArray => [
      ...prevArray,
      `Błąd podpisywania dokumentu o id: ${documentId} - `,
      error,
    ]);
  };

  if (signingInProgress) {
    return <SigningInProgressModal message={progress} />;
  }

  return (
    <>
      <ConfirmationButton
        className="mb-3"
        confirmation="Czy na pewno rozpocząć podpisywanie wszystkich dostępnych do podpisu skierowań?"
        variant="outline-primary"
        onOK={() => onSign()}
      >
        Podpisz wszystkie swoje skierowania
      </ConfirmationButton>
    </>
  );
};
